import React, { useMemo, useEffect, useRef, useContext } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  ModuleRegistry,
  AllCommunityModule,
  GridReadyEvent,
  GridApi,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import './AgTable.css';
import { ShoppingBag, ChevronLeft, ChevronRight } from 'lucide-react';
import { GlobalContext } from '../../context/Provider';

// Register AG Grid Community modules
ModuleRegistry.registerModules([AllCommunityModule]);

export interface AgTableProps {
  rowData: any[];
  columnDefs: ColDef[];
  title?: string;
  subtitle?: string;
  loading?: boolean;
  height?: string | number;
  autoHeight?: boolean;
  density?: 'compact' | 'normal' | 'comfortable';
  striped?: boolean;
  showRowHover?: boolean;
  stickyFirstColumn?: boolean;
  headerAction?: React.ReactNode;
  emptyMessage?: string;
  emptySubtitle?: string;
  emptyAction?: React.ReactNode;
  badge?: string | number;
  totalCount?: number;
  onRowClicked?: (event: any) => void;
  className?: string;
  animateRows?: boolean;
  rowSelection?: 'single' | 'multiple';
  onGridReady?: (params: GridReadyEvent) => void;
  // Built-in pagination support
  pagination?: boolean;
  page?: number;
  limit?: number;
  count?: number;
  onPageChange?: (page: number) => void;
  pageSizeOptions?: number[];
  onPageSizeChange?: (newLimit: number) => void;
  showPaginationDetails?: boolean;
  // Backwards compatible props
  searchable?: boolean;
  searchPlaceholder?: string;
  paginationPageSize?: number;
}

const AgTable: React.FC<AgTableProps> = ({
  rowData = [],
  columnDefs = [],
  title,
  subtitle,
  loading = false,
  height,
  autoHeight,
  density = 'normal',
  striped = false,
  showRowHover = true,
  stickyFirstColumn = false,
  headerAction,
  emptyMessage = 'No records found',
  emptySubtitle = 'Data will appear here as soon as new activity occurs.',
  emptyAction,
  badge,
  totalCount,
  onRowClicked,
  className = '',
  animateRows = true,
  rowSelection = 'single',
  onGridReady: parentOnGridReady,
  pagination = false,
  page = 1,
  limit = 10,
  count,
  onPageChange,
  pageSizeOptions = [10, 25, 50],
  onPageSizeChange,
  showPaginationDetails = true,
}) => {
  const gridApiRef = useRef<GridApi | null>(null);
  const globalContext = useContext(GlobalContext);

  // Auto-height is active when autoHeight is true, or height is 'auto' / not provided, or <= 10 rows
  const isAutoHeight =
    autoHeight !== undefined
      ? autoHeight
      : height === 'auto' || !height || height === '120px' || height === '380px' || (Array.isArray(rowData) && rowData.length > 0 && rowData.length <= 10);

  // Default column definition with proper minWidth, flex
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      filter: false,
      resizable: true,
      flex: 1,
      minWidth: 100,
      suppressMovable: true,
    };
  }, []);

  // Process column definitions: optionally pin the first column if stickyFirstColumn is true
  const processedColumnDefs = useMemo<ColDef[]>(() => {
    if (!stickyFirstColumn || !columnDefs.length) return columnDefs;
    return columnDefs.map((col, index) => {
      if (index === 0 && !col.pinned) {
        return { ...col, pinned: 'left' };
      }
      return col;
    });
  }, [columnDefs, stickyFirstColumn]);

  const handleGridReady = (params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    setTimeout(() => {
      params.api.sizeColumnsToFit();
    }, 30);
    if (parentOnGridReady) {
      parentOnGridReady(params);
    }
  };

  // Re-fit columns whenever rowData or columnDefs change
  useEffect(() => {
    if (gridApiRef.current) {
      const timer = setTimeout(() => {
        gridApiRef.current?.sizeColumnsToFit();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [rowData, columnDefs]);

  // Responsive column auto-fit on window resize
  useEffect(() => {
    const handleResize = () => {
      if (gridApiRef.current) {
        gridApiRef.current.sizeColumnsToFit();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Display count badge logic
  const displayCount =
    badge !== undefined ? badge : totalCount !== undefined ? totalCount : rowData?.length;

  // Pagination calculation
  const paginationTotal = count !== undefined ? count : totalCount !== undefined ? totalCount : rowData?.length;
  const totalPages = Math.max(1, Math.ceil(paginationTotal / limit));
  const currentPage = Math.max(1, Math.min(page, totalPages));

  const startRecord = paginationTotal === 0 ? 0 : (currentPage - 1) * limit + 1;
  const currentCount = Array.isArray(rowData) ? rowData.length : 0;
  const endRecord = currentCount > 0 ? (currentPage - 1) * limit + currentCount : Math.min(currentPage * limit, paginationTotal);

  const handlePageClick = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
    if (onPageChange) {
      onPageChange(newPage);
    } else if (globalContext?.onChangePagination) {
      globalContext.onChangePagination(newPage);
    }
  };

  // Generate page numbers with smart ellipsis: [1] [2] [3] [4] ... [56]
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= 6) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  const densityClass =
    density === 'compact'
      ? 'agtable-density-compact'
      : density === 'comfortable'
        ? 'agtable-density-comfortable'
        : '';

  const stripedClass = striped ? 'agtable-striped' : '';

  return (
    <div
      className={`agtable-card-wrapper ${densityClass} ${stripedClass} ${className}`}
    >
      {/* Top Header Section (Title + Badge + Subtitle + Action Button) */}
      {(title || headerAction) && (
        <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gradient-to-b from-white to-slate-50/40">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2.5 flex-wrap">
              {title && (
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight m-0">
                  {title}
                </h3>
              )}
            </div>
            {subtitle && (
              <p className="text-xs sm:text-sm text-slate-500 font-normal mt-0.5 m-0">
                {subtitle}
              </p>
            )}
          </div>

          {headerAction && (
            <div className="flex items-center gap-2.5">
              {headerAction}
            </div>
          )}
        </div>
      )}

      {/* Main Table Content Container */}
      <div className="w-full overflow-x-auto">
        {loading ? (
          /* Shimmer Loading Skeleton */
          <div className="bg-white relative w-full">
            <div className="h-12 bg-[#F8FAFC] border-b border-slate-100 flex items-center px-6 gap-6 animate-pulse">
              <div className="w-16 h-3.5 bg-slate-200 rounded"></div>
              <div className="w-40 h-3.5 bg-slate-200 rounded"></div>
              <div className="w-28 h-3.5 bg-slate-200 rounded hidden sm:block"></div>
              <div className="w-24 h-3.5 bg-slate-200 rounded hidden md:block"></div>
              <div className="w-20 h-3.5 bg-slate-200 rounded ml-auto"></div>
            </div>
            <div className="divide-y divide-slate-100">
              {[1, 2, 3, 4, 5].map((item) => (
                <div
                  key={item}
                  className="h-15 px-6 flex items-center gap-6 animate-pulse"
                >
                  <div className="w-8 h-4 bg-slate-200 rounded"></div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-200 shrink-0"></div>
                    <div className="flex flex-col gap-1.5">
                      <div className="w-36 h-3.5 bg-slate-200 rounded"></div>
                      <div className="w-24 h-2.5 bg-slate-100 rounded"></div>
                    </div>
                  </div>
                  <div className="w-24 h-3.5 bg-slate-100 rounded hidden sm:block"></div>
                  <div className="w-20 h-3.5 bg-slate-100 rounded hidden md:block"></div>
                  <div className="w-24 h-6 bg-slate-100 rounded-full ml-auto"></div>
                </div>
              ))}
            </div>
          </div>
        ) : rowData.length === 0 ? (
          /* Empty State */
          <div className="py-16 px-6 text-center bg-white flex flex-col items-center justify-center">
            <div className="w-14 h-14 rounded-2xl bg-sky-50 text-[#1AA7DE] border border-sky-100 flex items-center justify-center mb-3.5 shadow-2xs">
              <ShoppingBag className="w-7 h-7" />
            </div>
            <p className="text-base font-bold text-slate-800 m-0">{emptyMessage}</p>
            <p className="text-xs sm:text-sm text-slate-400 max-w-sm mt-1 mb-0 leading-relaxed">
              {emptySubtitle}
            </p>
            {emptyAction && <div className="mt-4">{emptyAction}</div>}
          </div>
        ) : (
          /* AG Grid Quartz Container */
          <div
            className="ag-theme-quartz w-full"
            style={{
              height: isAutoHeight ? undefined : height || '540px',
              width: '100%',
            }}
          >
            <AgGridReact
              rowData={rowData}
              columnDefs={processedColumnDefs}
              defaultColDef={defaultColDef}
              pagination={false}
              domLayout={isAutoHeight ? 'autoHeight' : undefined}
              rowHeight={density === 'compact' ? 52 : density === 'comfortable' ? 72 : 68}
              headerHeight={48}
              autoSizeStrategy={{
                type: 'fitGridWidth',
                defaultMinWidth: 100,
              }}
              suppressMovableColumns={true}
              suppressRowDrag={true}
              suppressDragLeaveHidesColumns={true}
              onGridReady={handleGridReady}
              onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
              onGridSizeChanged={(params) => params.api.sizeColumnsToFit()}
              animateRows={animateRows}
              rowSelection={rowSelection as any}
              onRowClicked={onRowClicked}
            />
          </div>
        )}
      </div>

      {/* Built-in Bottom Pagination Footer (when pagination=true or count provided) */}
      {(pagination || count !== undefined) && paginationTotal > 0 && (
        <div className="px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white">
          {/* Left: Showing X to Y of Z results + Optional Page Size Selector */}
          <div className="flex items-center gap-3 flex-wrap">
            {showPaginationDetails && (
              <div className="text-xs sm:text-sm text-slate-500 font-medium whitespace-nowrap">
                Showing <span className="font-bold text-slate-900">{startRecord}</span> to{' '}
                <span className="font-bold text-slate-900">{endRecord}</span> of{' '}
                <span className="font-bold text-slate-900">{paginationTotal}</span> results
              </div>
            )}

            {onPageSizeChange && pageSizeOptions.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <span>Rows:</span>
                <select
                  value={limit}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                >
                  {pageSizeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Right: Previous / Page Numbers / Next Buttons */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2 ml-auto">
              {/* Previous Button - Always whitespace-nowrap */}
              <button
                type="button"
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage <= 1}
                aria-label="Previous page"
                className="whitespace-nowrap inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 hover:text-[#1AA7DE] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE]"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>Previous</span>
              </button>

              {/* Number Buttons */}
              <div className="flex items-center gap-1 mx-1">
                {getPageNumbers().map((item, index) => {
                  if (item === '...') {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="w-7 h-7 flex items-center justify-center text-slate-400 text-xs font-semibold select-none"
                      >
                        ...
                      </span>
                    );
                  }

                  const pageNum = Number(item);
                  const isActive = pageNum === currentPage;

                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => handlePageClick(pageNum)}
                      aria-label={`Page ${pageNum}`}
                      aria-current={isActive ? 'page' : undefined}
                      className={`w-7 h-7 flex items-center justify-center text-xs font-bold rounded-lg transition-all ${isActive
                          ? 'bg-[#1AA7DE] text-white shadow-2xs'
                          : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              {/* Next Button - Always whitespace-nowrap */}
              <button
                type="button"
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
                className="whitespace-nowrap inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 hover:text-[#1AA7DE] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE]"
              >
                <span>Next</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgTable;
